/**
 * Fechas y horas del consultorio (America/Mexico_City).
 */
import {
  addDays,
  addMonths,
  endOfISOWeek,
  endOfMonth,
  endOfWeek,
  startOfISOWeek,
  startOfWeek,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { es } from 'date-fns/locale';

export const CLINIC_TZ = 'America/Mexico_City';

function noonInClinic(ymd) {
  return fromZonedTime(`${String(ymd).slice(0, 10)}T12:00:00`, CLINIC_TZ);
}

export function todayYmdInClinic() {
  return formatInTimeZone(new Date(), CLINIC_TZ, 'yyyy-MM-dd');
}

/** YYYY-MM-DD del instante o fecha en zona del consultorio. */
export function calendarDayKeyInClinic(date) {
  if (!date) return '';
  if (typeof date === 'string') {
    const s = date.trim();
    // Solo fecha (citas en BD): sin conversión de zona.
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(0, 10);
    return formatInTimeZone(new Date(s), CLINIC_TZ, 'yyyy-MM-dd');
  }
  return formatInTimeZone(date, CLINIC_TZ, 'yyyy-MM-dd');
}

/** Día calendario del evento (prioriza `date` de la cita en BD). */
export function calendarEventDayKeyInClinic(event) {
  const apptDate = event?.appointment?.date;
  if (apptDate) return String(apptDate).slice(0, 10);
  return calendarDayKeyInClinic(event?.start_at);
}

/** Orden cronológico por hora local del consultorio. */
export function compareCalendarEventsByClinicTime(a, b) {
  const ta = Date.parse(a?.start_at || '');
  const tb = Date.parse(b?.start_at || '');
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return ta - tb;
}

/** Clave de orden para citas en BD (YYYY-MM-DD + HH:MM, sin parseo UTC). */
export function appointmentSortKey(appointment) {
  const dateYmd = calendarDayKeyInClinic(appointment?.date);
  const timeRaw = String(appointment?.time || '').trim();
  const timeHm = /^\d{1,2}:\d{2}/.test(timeRaw) ? timeRaw.slice(0, 5) : '';
  return { dateYmd, timeHm };
}

/** Orden ascendente por día y hora del consultorio (listados admin). */
export function compareAppointmentsByClinicDateTime(a, b) {
  const ka = appointmentSortKey(a);
  const kb = appointmentSortKey(b);
  if (!ka.dateYmd && !kb.dateYmd) return 0;
  if (!ka.dateYmd) return 1;
  if (!kb.dateYmd) return -1;
  if (ka.dateYmd !== kb.dateYmd) return ka.dateYmd.localeCompare(kb.dateYmd);
  if (!ka.timeHm && !kb.timeHm) return 0;
  if (!ka.timeHm) return 1;
  if (!kb.timeHm) return -1;
  return ka.timeHm.localeCompare(kb.timeHm);
}

/**
 * Convierte el día elegido en react-day-picker a YYYY-MM-DD (zona clínica).
 * Evita guardar el día anterior cuando el picker entrega medianoche UTC.
 */
export function datePickerToClinicYmd(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const yu = date.getUTCFullYear();
  const mu = date.getUTCMonth();
  const du = date.getUTCDate();
  const useUtc =
    yu > y || (yu === y && mu > m) || (yu === y && mu === m && du > d);
  if (useUtc) {
    return `${yu}-${String(mu + 1).padStart(2, '0')}-${String(du).padStart(2, '0')}`;
  }
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function formatUtcIsoInClinic(iso, pattern) {
  if (!iso) return '';
  return formatInTimeZone(new Date(iso), CLINIC_TZ, pattern, { locale: es });
}

export function formatYmdClinic(ymd, pattern) {
  return formatInTimeZone(noonInClinic(ymd), CLINIC_TZ, pattern, { locale: es });
}

export function formatAppointmentDateOnlyInClinic(dateOnly, pattern) {
  const ymd =
    typeof dateOnly === 'string' ? dateOnly.slice(0, 10) : calendarDayKeyInClinic(dateOnly);
  return formatYmdClinic(ymd, pattern);
}

export function addCalendarDaysYmd(ymd, days) {
  const next = addDays(noonInClinic(ymd), days);
  return formatInTimeZone(next, CLINIC_TZ, 'yyyy-MM-dd');
}

export function addCalendarMonthsYmd(ymd, months) {
  const next = addMonths(noonInClinic(ymd), months);
  return formatInTimeZone(next, CLINIC_TZ, 'yyyy-MM-dd');
}

export function firstOfMonthYmd(ymd) {
  const s = String(ymd).slice(0, 10);
  return `${s.slice(0, 7)}-01`;
}

export function isoWeekBoundsYmd(ymd) {
  const anchor = noonInClinic(ymd);
  const start = startOfISOWeek(anchor);
  const end = endOfISOWeek(anchor);
  return {
    weekStartYmd: formatInTimeZone(start, CLINIC_TZ, 'yyyy-MM-dd'),
    weekEndYmd: formatInTimeZone(end, CLINIC_TZ, 'yyyy-MM-dd'),
  };
}

export function monthGridBoundsYmd(ymd) {
  const first = noonInClinic(firstOfMonthYmd(ymd));
  const gridStart = startOfWeek(first, { weekStartsOn: 0 });
  const last = endOfMonth(first);
  const gridEnd = endOfWeek(last, { weekStartsOn: 0 });
  return {
    gridStartYmd: formatInTimeZone(gridStart, CLINIC_TZ, 'yyyy-MM-dd'),
    gridEndYmd: formatInTimeZone(gridEnd, CLINIC_TZ, 'yyyy-MM-dd'),
  };
}

export function eachYmdInclusive(startYmd, endYmd) {
  const out = [];
  let cur = startYmd;
  while (cur <= endYmd) {
    out.push(cur);
    cur = addCalendarDaysYmd(cur, 1);
  }
  return out;
}

/** 1 = lunes … 7 = domingo (como working_days del backend). */
export function calendarIsoWeekdayFromYmd(ymd) {
  return parseInt(formatInTimeZone(noonInClinic(ymd), CLINIC_TZ, 'i'), 10);
}

export function ymdToDatePickerDate(ymd) {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}

export function getCalendarApiRangeUtc(focusYmd, view) {
  let startYmd;
  let endYmd;
  if (view === 'day') {
    startYmd = focusYmd;
    endYmd = focusYmd;
  } else if (view === 'week') {
    const b = isoWeekBoundsYmd(focusYmd);
    startYmd = b.weekStartYmd;
    endYmd = b.weekEndYmd;
  } else {
    const b = monthGridBoundsYmd(focusYmd);
    startYmd = b.gridStartYmd;
    endYmd = b.gridEndYmd;
  }
  return {
    start: fromZonedTime(`${startYmd}T00:00:00`, CLINIC_TZ),
    end: fromZonedTime(`${endYmd}T23:59:59.999`, CLINIC_TZ),
  };
}
